import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL_AUTH } from '../config/api';
import COUNTRY_CODES, { DEFAULT_COUNTRY_CODE } from '../utils/countryCodes';

function getFlagEmoji(countryCode) {
  try {
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  } catch {
    return '';
  }
}

export default function LoginPage() {
  const [loginMode, setLoginMode] = useState('phone');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [countrySearch, setCountrySearch] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const selectedCountry = useMemo(
    () => COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0],
    [countryCode]
  );

  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return COUNTRY_CODES;
    const q = countrySearch.toLowerCase();
    return COUNTRY_CODES.filter(
      c => c.name.toLowerCase().includes(q) || c.phoneCode.includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [countrySearch]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCountryDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loginMode === 'email') {
      if (!email) {
        setError('Please enter your email address.');
        return;
      }
    } else {
      if (!phone) {
        setError('Please enter your phone number.');
        return;
      }
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    setError('');
    setLoading(true);

    let identifier;
    if (loginMode === 'email') {
      identifier = email;
    } else {
      const digits = phone.replace(/\D/g, '');
      identifier = selectedCountry.phoneCode + digits;
    }

    try {
      const response = await fetch(`${API_BASE_URL_AUTH}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email: identifier, password }),
      });

      const data = await response.json();

      if (response.ok && data.status === 1) {
        login(data.token, data.user_data);
        navigate('/');
      } else {
        setError(data.message || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred during login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-900 to-red-700 px-4">
      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm">
        <h2 className="text-2xl font-bold mb-6 text-center text-red-800">Login</h2>
        {error && <div className="mb-4 text-red-500 text-sm p-2 bg-red-100 rounded">{error}</div>}

        <div className="flex mb-4 bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => { setLoginMode('phone'); setError(''); }}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition ${
              loginMode === 'phone'
                ? 'bg-white text-red-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Phone
          </button>
          <button
            type="button"
            onClick={() => { setLoginMode('email'); setError(''); }}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition ${
              loginMode === 'email'
                ? 'bg-white text-red-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Email
          </button>
        </div>

        {loginMode === 'email' ? (
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-400"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              disabled={loading}
            />
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="phone">Phone Number</label>
            <div className="relative flex" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                className="flex items-center gap-1 px-2 py-2 border border-r-0 rounded-l bg-gray-100 hover:bg-gray-200 text-sm whitespace-nowrap h-[42px] min-w-[90px] justify-center focus:outline-none focus:ring-2 focus:ring-red-400"
                disabled={loading}
              >
                <span className="text-base">{getFlagEmoji(selectedCountry.code)}</span>
                <span>+{selectedCountry.phoneCode}</span>
                <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {countryDropdownOpen && (
                <div className="absolute z-50 top-full left-0 mt-1 w-72 bg-white border rounded-lg shadow-lg max-h-60 overflow-hidden">
                  <div className="p-2 border-b sticky top-0 bg-white">
                    <input
                      type="text"
                      placeholder="Search country..."
                      className="w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-red-400"
                      value={countrySearch}
                      onChange={e => setCountrySearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="overflow-y-auto max-h-48">
                    {filteredCountries.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-400">No countries found</div>
                    ) : (
                      filteredCountries.map(c => (
                        <button
                          key={c.code}
                          type="button"
                          onClick={() => {
                            setCountryCode(c.code);
                            setCountryDropdownOpen(false);
                            setCountrySearch('');
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-red-50 transition ${
                            c.code === countryCode ? 'bg-red-50 font-semibold' : ''
                          }`}
                        >
                          <span className="text-base">{getFlagEmoji(c.code)}</span>
                          <span className="flex-1 truncate text-left">{c.name}</span>
                          <span className="text-gray-400">+{c.phoneCode}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

              <input
                id="phone"
                type="tel"
                placeholder="e.g. 3429686"
                className="flex-1 px-3 py-2 border rounded-r focus:outline-none focus:ring-2 focus:ring-red-400 min-w-0"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                autoComplete="tel-national"
                disabled={loading}
              />
            </div>
            {phone && (
              <p className="text-gray-400 text-xs mt-1">
                Full number: +{selectedCountry.phoneCode} {phone}
              </p>
            )}
          </div>
        )}

        <div className="mb-6">
          <label className="block text-gray-700 mb-2" htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-400"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={loading}
          />
        </div>
        <button type="submit" className="w-full bg-red-800 text-white py-2 rounded hover:bg-red-900 transition" disabled={loading}>
          {loading ? 'Logging in...' : 'Login'}
        </button>
        <p className="text-center text-sm mt-3">
          <Link to="/forgot-password" className="text-red-800 hover:underline text-sm">Forgot your password?</Link>
        </p>
        <p className="text-center text-sm text-gray-600 mt-2">
          Don't have an account? <Link to="/register" className="text-red-800 hover:underline">Register here</Link>
        </p>
      </form>
    </div>
  );
}
